import {
  Card,
  CardBody,
  Container,
  CardHeader,
  Stack,
  IconButton,
  useDisclosure,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Heading,
  Input,
  Divider,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  useToast, FormErrorMessage,
  FormHelperText,
  Text,
  Checkbox
} from "@chakra-ui/react";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import CustomTable from "components/table/CustomTable";
import { ChevronRightIcon, DeleteIcon, EditIcon, SmallAddIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/store.hook";
import { Link } from "react-router-dom";
import { addDocument, removeDocument, setDocumentInEditingId, updateDocument } from "store/reducers/documents.slice";
import { TemplateModalMode } from "types/TemplateModalMode";
import { Document } from "types/Document";
import { deleteDocument, patchDocument, postCreateDocument } from "service/api/document.api";
import { toLocalDateTimeReadableString, toLocalDateTimeString } from "utils/datesManagement";
import { Breadcrumb } from "types/Breadcrumb";


type TableData = {
  _id: string;
  code: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};
const columnHelper = createColumnHelper<TableData>();

type DocumentPageProps = {
  isUserAdmin: boolean;
  isUserAdminRolesEditor: boolean;
};


/**
 * DocumentPage component for managing documents.
 * 
 * @param {DocumentPageProps} props - The properties for the DocumentPage component.
 * @param {boolean} props.isUserAdmin - Indicates if the user is an admin.
 * @param {boolean} props.isUserAdminRolesEditor - Indicates if the user has roles editor permissions.
 * 
 * @returns {JSX.Element} The rendered DocumentPage component.
 * 
 * @remarks
 * This component uses various hooks and state management to handle document creation, editing, and deletion.
 * It also includes form validation and displays toast notifications for success and error messages.
 * 
 * @component
 * 
 * @example
 * ```tsx
 * <DocumentPage isUserAdmin={true} isUserAdminRolesEditor={false} />
 * ```
 */
export default function DocumentPage({ isUserAdmin, isUserAdminRolesEditor }: DocumentPageProps) {

  const documents = useSelector((state: any) => state.documents.documents);
  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [formValues, setFormValues] = useState<any>({});
  const [formMode, setFormMode] = useState<TemplateModalMode>(TemplateModalMode.EDIT);
  const [insertionsNumber, setInsertionsNumber] = useState<number>(0);
  const [isCodeWrong, setIsCodeWrong] = useState<boolean>(false);
  const [isInsertionsWrong, setIsInsertionsWrong] = useState<boolean>(false);

  const { register, handleSubmit, reset, control } = useForm({
    defaultValues: {
      _id: "",
      code: "",
      title: "",
      subtitle: "",
      description: "",
      documentId: "",
    },
    values: formValues,
  });
  const toast = useToast()

  const columns = [
    columnHelper.accessor("code", {
      cell: (info: any) => info.getValue(),
      header: `Codice template`,
    }),
    columnHelper.accessor("title", {
      cell: (info: any) => info.getValue(),
      header: `Titolo template`,
    }),
    columnHelper.accessor("updatedAt", {
      cell: (info: any) => toLocalDateTimeReadableString(info.getValue()),
      header: `Data ultima modifica`,
    }),
    columnHelper.accessor("_id", {
      cell: ({ row }) => (
        <Stack spacing={2} direction="row" align="center">
          <IconButton
            colorScheme="yellow"
            aria-label="Edit"
            size="sm"
            onClick={() => showModal(row.original)}
            icon={<EditIcon />}
          />
          <IconButton
            colorScheme="red"
            aria-label="Delete"
            size="sm"
            onClick={() => { showModal(row.original, TemplateModalMode.DELETE) }}
            icon={<DeleteIcon />} />
          <Button
            colorScheme="blue"
            aria-label="Go to subtargets"
            size="sm"
            as={Link}
            to={`${row.original.code}/targets`}
            onClick={() => onClickHandler(row.original._id)}
          >
            Obiettivi
            <ChevronRightIcon />
          </Button>
        </Stack>
      ),
      header: ``,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
  ];

  const onClickHandler = (documentId: string) => {
    dispatch(setDocumentInEditingId({ documentId }));
  }
  const onSubmit: SubmitHandler<any> = (data: any) => {
    switch (formMode) {
      case TemplateModalMode.ADD:
        postCreateDocument(
          {
            title: data.title,
            description: data.description,
            subtitle: data.subtitle,
            code: data.code,
            documentId: data.documentId,
            isActive: data.isActive,
          }
        ).then((response) => {
          dispatch(addDocument(
            {
              _id: response._id,
              title: response.title,
              description: response.description,
              subtitle: response.subtitle,
              code: response.code,
              targets: response.targets,
              isActive: response.isActive,
            }
          ))
          toast({
            title: "Documento creato con successo",
            status: "success",
            duration: 6000,
            isClosable: true,
            position: "top"
          })
        }).catch((error) => {
          const errorMsg = error.response.data.message ?? "";
          toast({
            title: `Errore nella creazione del documento: ${errorMsg}`,
            status: "error",
            duration: 6000,
            isClosable: true,
            position: "top"
          })
        })

        break;
      case TemplateModalMode.EDIT:
        const documentId = data._id;
        const title = data.title;
        const description = data.description;
        const subtitle = data.subtitle;
        const code = data.code;
        const isActive = data.isActive;
        patchDocument(documentId, title, subtitle, description, code, isActive).then((response: any) => {
          dispatch(updateDocument(
            {
              documentId: documentId,
              title: title,
              description: description,
              subtitle: subtitle,
              code: code,
              isActive: isActive
            }
          ))

          toast({
            title: "Documento modificato con successo",
            status: "success",
            duration: 6000,
            isClosable: true,
            position: "top"
          })

        }).catch((error: any) => {

          toast({
            title: "Errore nella modifica del documento",
            status: "error",
            duration: 6000,
            isClosable: true,
            position: "top"
          })

        })


        break;
      case TemplateModalMode.DELETE:

        deleteDocument(data._id).then((response: any) => {
          dispatch(removeDocument({
            documentId: data._id
          }
          ))

          toast({
            title: "Documento eliminato con successo",
            status: "success",
            duration: 6000,
            isClosable: true,
            position: "top"
          })
        }).catch((error: any) => {

          toast({
            title: "Errore nell'eliminazione del documento",
            status: "error",
            duration: 6000,
            isClosable: true,
            position: "top"
          })

        })
        break;
    }
    onClose();
  }
  const showModal = (itemValues: any, mode: TemplateModalMode = TemplateModalMode.EDIT) => {

    if (mode == TemplateModalMode.EDIT) {
      setFormMode(TemplateModalMode.EDIT);
      const obj = {
        ...itemValues,
        updatedAt: toLocalDateTimeString(itemValues.updatedAt),
        createdAt: toLocalDateTimeString(itemValues.createdAt),
        timePeriods: itemValues.timePeriods
      }
      setFormValues(obj);

    }
    else if (mode == TemplateModalMode.ADD) {
      setFormValues({
        _id: "",
        code: "",
        title: "",
        subtitle: "",
        description: "",
        documentId: "",
      });
      reset();
      setFormMode(TemplateModalMode.ADD);

    } else if (mode == TemplateModalMode.DELETE) {
      setFormMode(TemplateModalMode.DELETE);
      const obj = {
        ...itemValues,
        updatedAt: toLocalDateTimeString(itemValues.updatedAt),
        createdAt: toLocalDateTimeString(itemValues.createdAt),
        timePeriods: itemValues.timePeriods
      }
      setFormValues(obj);
    }
    onOpen();
  }

  const handleInputCodeChange = (e: any) => {

    const code = e.target.value;
    if (code && code.match(/^[A-Za-z0-9]{1,16}$/
    ) || code == "") {
      setIsCodeWrong(false);
    } else {
      setIsCodeWrong(true);
    }

  }

  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Lista documenti", link: "/documents", value: "", isActive: false },
    ]
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size={"2xl"}>
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onSubmit)} >
            <ModalHeader as="h2">
              {
                formMode == TemplateModalMode.DELETE &&
                <Text>Sei sicuro di voler eliminare il template?</Text>
              }
              {
                formMode == TemplateModalMode.ADD &&
                <Text>Aggiungi un nuovo template</Text>
              }
              {
                formMode == TemplateModalMode.EDIT &&
                <Text>Modifica il template {formValues.code}</Text>
              }
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              {
                formMode != TemplateModalMode.ADD &&
                <>
                  <Card variant={"filled"}>
                    <CardBody>
                      <Stack direction="row">
                        <FormControl mb={5}>
                          <FormLabel>Codice</FormLabel>
                          <Input placeholder="Inserisci il codice" size={"xs"}
                            isRequired={true}
                            isReadOnly={formMode == TemplateModalMode.DELETE || formMode == TemplateModalMode.EDIT}
                            {
                            ...register(`code`, { required: true })
                            }
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel>Data di creazione</FormLabel>
                          <Input size={"xs"} required={true}
                            isReadOnly={true}
                            type="datetime-local"
                            {
                            ...register(`createdAt`, { required: true })
                            }
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Ultimo aggiornamento</FormLabel>
                          <Input size={"xs"} required={true}
                            isReadOnly={true}
                            type="datetime-local"
                            {
                            ...register(`updatedAt`, { required: true })
                            }
                          />
                        </FormControl>
                      </Stack>
                    </CardBody>
                  </Card>
                  <Divider my={2} />
                </>
              }

              {
                formMode == TemplateModalMode.ADD &&
                <FormControl mb={5} isInvalid={isCodeWrong} isRequired>
                  <FormLabel htmlFor="input-code" display={"inline"}>Codice </FormLabel><FormHelperText display={"inline"}>(solo valori alfanumerici ed univoci)</FormHelperText>
                  <Input placeholder="Inserisci il codice" size={"md"}
                    id="input-code"
                    pattern="[A-Za-z0-9]{1,32}"
                    isRequired={true}
                    onChangeCapture={handleInputCodeChange}
                    errorBorderColor="red.300"
                    _invalid={
                      {
                        borderColor: "red.300",
                        bg: "rgba(255, 0, 0, 0.1)",
                      }
                    }
                    {
                    ...register(`code`, {
                      required: true,
                    })
                    }
                  />
                  {
                    isCodeWrong &&
                    <FormErrorMessage>
                      Il codice deve contenere solo caratteri alfanumerici e deve essere lungo al massimo 16 caratteri.
                    </FormErrorMessage>
                  }
                </FormControl>
              }

              <FormControl mb={5} isRequired>
                <FormLabel>Titolo</FormLabel>
                <Textarea placeholder='Inserisci il titolo' size='md' required={true}
                  isReadOnly={formMode == TemplateModalMode.DELETE}
                  isRequired={true}
                  {
                  ...register(`title`, { required: true })
                  }
                />
              </FormControl>

              <FormControl mb={5} isRequired>
                <FormLabel>Sottotitolo</FormLabel>
                <Textarea placeholder='Inserisci il sottotitolo' size='md' required={true}
                  isRequired={true}
                  isReadOnly={formMode == TemplateModalMode.DELETE}
                  {
                  ...register(`subtitle`, { required: true })
                  }
                />
              </FormControl>

              <FormControl mb={5} isRequired>
                <FormLabel>Descrizione</FormLabel>
                <Textarea placeholder='Inserisci la descrizione' size='md' required={true}
                  isRequired={true}
                  isReadOnly={formMode == TemplateModalMode.DELETE}
                  {
                  ...register(`description`, { required: true })
                  }
                />
              </FormControl>

              <FormControl mb={5}>
                <FormLabel>Attivo</FormLabel>
                <Controller
                  control={control}
                  name={'isActive'}
                  render={({ field: { onChange, value, ref } }) => (
                    <Checkbox
                      onChange={onChange}
                      ref={ref}
                      isChecked={value}
                      id={`isActive`}
                    >
                      Consenti la modifica del documento
                    </Checkbox>
                  )}
                />
              </FormControl>

              {
                formMode == TemplateModalMode.ADD &&
                <>
                  {
                    [...Array(insertionsNumber >= 0 ? insertionsNumber : 0)]?.map((_, index) => (
                      <FormControl mb={5}>
                        <FormLabel>
                          Etichetta valore {index + 1}
                        </FormLabel>
                        <Input size='md'
                          isRequired={true}
                          key={index}
                          placeholder={`Inserisci etichetta valore ${index + 1}`}
                          {...register(`insertionslabels.${index}.value`)}
                        />
                      </FormControl>
                    ))}
                </>
              }

              {
                formMode == TemplateModalMode.ADD &&
                <FormControl mb={5}>
                  <FormLabel htmlFor="select-document">Copia struttura di un altro documento</FormLabel>
                  <Select required={false}
                    id="select-document"
                    placeholder={"Seleziona documento del quale copiare la struttura"}
                    {
                    ...register(`documentId`, { required: false })
                    }
                  >
                    {documents?.map((document: Document, index: number) => {
                      return (
                        document?._id &&
                        <option key={`dcmntcp-${index}`} value={document._id}>
                          {document?.title} - {document?.code} - {document?.createdAt}
                        </option>
                      );
                    })}
                  </Select>
                  <FormHelperText>Replica la struttura (obiettivi, sotto-obiettivi, azioni, indici) del documento selezionato.</FormHelperText>
                </FormControl>
              }


              <Input size='md'
                type={"hidden"}
                aria-hidden={true}
                isReadOnly={true}
                {
                ...register(`_id`, { required: false })
                }
              />
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={onClose}>
                Chiudi
              </Button>
              {formMode != TemplateModalMode.DELETE ?
                <Button colorScheme="green" mr={3} type="submit">
                  {
                    formMode == TemplateModalMode.EDIT ? "Salva modifiche" : "Aggiungi"
                  }
                </Button>
                :
                <Button colorScheme="red" mr={3} type="submit">
                  Elimina
                </Button>
              }
            </ModalFooter>

          </form>
        </ModalContent>
      </Modal >
      <section className="dashboard-first-section">
        <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
          <MyBreadcrumb breadcrumb={breadcrumb} />


          <Card minH={"60vh"}>
            <CardHeader>
              <Heading as="h1" size="xl" >
                Documenti
              </Heading>
              <Button size={"xs"} colorScheme="green" alignContent={"end"} float={"right"} disabled={true} onClick={() => showModal({}, TemplateModalMode.ADD)}>
                Aggiungi documento
                <SmallAddIcon />
              </Button>
            </CardHeader>
            <CardBody>
              <CustomTable data={documents} columns={columns} />
            </CardBody>
          </Card>
        </Container>
      </section>
    </>
  );
}